import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Breadcrumb as ListBreadcrumb, ListItem } from '@thd-olt-component-react/list';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useProduct, useSearch } from '@thd-nucleus/data-sources';
import { copy } from '@thd-olt-functional/utils';
import { EVENT_DIMENSION_NAME } from '../constants';
import styles from './breadcrumb.module.scss';

const imageContainer = styles['breadcrumbs__image-container'];

const normalizeBreadcrumb = (crumb) => {
  return {
    id: null,
    name: null,
    label: null,
    refinementKey: null,
    dimensionName: 'Category',
    ...crumb
  };
};

const parseSearchModelForCrumbs = (crumbs = [], keyword, returnAll = false) => {
  if (keyword) {
    const textSearchCrumb = {
      url: '',
      label: 'Text Search'
    };

    let label;
    try {
      label = decodeURI(keyword);
    } catch (err) {
      label = keyword;
    }
    const keywordCrumb = {
      url: `/s/${keyword}?NCNI-5`,
      label
    };
    if (!crumbs.length) {
      crumbs.unshift(textSearchCrumb, keywordCrumb);
    } else {
      const textSearchCrumbIndex = crumbs.findIndex((crumb) => {
        return crumb.label === textSearchCrumb.label;
      });
      if (textSearchCrumbIndex === -1) {
        crumbs.unshift(textSearchCrumb, keywordCrumb);
      }
    }
  }
  if (crumbs.length > 0) {
    const hasCategory = crumbs.filter((crumb) => (crumb.dimensionName || '').toLowerCase() === 'category').length > 1;
    const breadcrumbs = hasCategory && !returnAll
      ? crumbs.filter((crumb) => (crumb.dimensionName || '').toLowerCase() === 'category')
      : crumbs.filter((crumb) => !crumb.deselectUrl);
    return breadcrumbs.map((crumb) => normalizeBreadcrumb(crumb));
  }
  return null;
};

const hasNoCategoryDimension = (breadcrumbs = []) => {
  return breadcrumbs.filter(
    (crumb) => (crumb.dimensionName || '').toLowerCase() === 'category'
  ).length === 0;
};

const filterEventBreadcrumbsForMobile = (breadcrumbs = [], shouldShowEventOnly) => {
  if (shouldShowEventOnly) {
    return breadcrumbs.filter((breadcrumb) => {
      return breadcrumb.dimensionName === EVENT_DIMENSION_NAME;
    });
  }
  return breadcrumbs.filter((breadcrumb) => {
    return breadcrumb.dimensionName !== EVENT_DIMENSION_NAME;
  });

};

const Breadcrumbs = (props) => {

  const { store, channel } = useContext(ExperienceContext);
  const { storeId } = store;

  const {
    className,
    hideOnNoCategory,
    itemId,
    keyword,
    navParam,
    rounded,
    showInternet,
    shouldShowEventOnly
  } = props;

  let crumbs;

  // eslint-disable-next-line
  const isControlledData = typeof props.data !== 'undefined'|| typeof props.loading !== 'undefined';
  const isMobile = channel === 'mobile';

  const skipSearch = isControlledData || (!keyword && !navParam);

  const productResponse = useProduct({ itemId, domains: ['taxonomy'], skip: !itemId || isControlledData });
  const searchResponse = useSearch({ keyword, navParam, domains: ['taxonomy'], skip: skipSearch });

  let data;
  // eslint-disable-next-line
  if (typeof props.data !== 'undefined') {
    ({ data } = props);
  } else if (itemId) {
    ({ data } = productResponse);
  } else {
    ({ data } = searchResponse);
  }

  useEffect(() => {
    if (crumbs) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('breadcrumbs.ready', { crumbs });
    }
  }, [data, crumbs]);

  if (!data) return null;
  let breadCrumbs = [];
  let searchCrumbs = null;
  if (data?.product?.taxonomy?.breadCrumbs) {
    breadCrumbs = copy(data?.product?.taxonomy?.breadCrumbs).data;
    breadCrumbs = isMobile ? filterEventBreadcrumbsForMobile(breadCrumbs, shouldShowEventOnly) : breadCrumbs;
  } else if (data?.searchModel?.taxonomy?.breadCrumbs) {
    breadCrumbs = copy(data?.searchModel?.taxonomy?.breadCrumbs).data;
  }

  if (hideOnNoCategory && hasNoCategoryDimension(breadCrumbs)) return null;

  if (itemId) {
    crumbs = (breadCrumbs || []).map((crumb) => {
      return normalizeBreadcrumb(crumb);
    });
  } else {
    const { keyword: keywordFromResponse } = data?.searchModel?.searchReport || {};
    crumbs = parseSearchModelForCrumbs(breadCrumbs, keywordFromResponse);
  }

  if (!crumbs || (crumbs && !crumbs.length)) {
    return null;
  }

  const crumbAttribute = crumbs[0].name ? 'name' : 'label';

  if (crumbs[0][crumbAttribute] !== 'Home') {
    const homePage = {
      url: '/'
    };
    homePage[crumbAttribute] = 'Home';
    crumbs.unshift(homePage);
  }

  if (isMobile && shouldShowEventOnly) {
    crumbs.shift();
  }

  if (showInternet) {
    const internetNumber = {
      isInternetNumber: true,
      label: `Internet #${itemId}`,
    };
    crumbs.push(internetNumber);
  }

  const breadcrumbClasses = classNames(
    [styles.breadcrumbs__nowrap],
    className, {
      [styles['breadcrumbs__nowrap--rounded']]: rounded
    }
  );

  return (
    <div className={styles.breadcrumbs}>
      <div className={breadcrumbClasses}>
        {crumbs.map((crumb, crumbIndex) => {
          let {
            dimensionName, isInternetNumber, creativeIconUrl, browseUrl, url
          } = crumb;
          return (
            <div
              key={crumbIndex}
              className={classNames({
                [styles.breadcrumb__item]: true,
                [styles['breadcrumb__item--not-a-link']]: isInternetNumber,
                [styles['breadcrumb__item--bold']]: dimensionName === EVENT_DIMENSION_NAME,
                [styles['breadcrumb__item--pipe']]: dimensionName === EVENT_DIMENSION_NAME
              })}
            >
              {isInternetNumber && (
                `Internet #${itemId}`
              )}
              {!isInternetNumber && creativeIconUrl && (
                <div className={imageContainer} style={{ backgroundImage: `url(${creativeIconUrl})` }} />
              )}
              {!isInternetNumber && (
                <a href={browseUrl || url}>{crumb[crumbAttribute]}</a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

Breadcrumbs.displayName = 'Breadcrumbs';

Breadcrumbs.propTypes = {
  className: PropTypes.string,
  hideOnNoCategory: PropTypes.bool,
  itemId: PropTypes.string,
  keyword: PropTypes.string,
  navParam: PropTypes.string,
  rounded: PropTypes.bool,
  showInternet: PropTypes.bool,
  data: PropTypes.shape({}),
  loading: PropTypes.bool,
  shouldShowEventOnly: PropTypes.bool
};

Breadcrumbs.defaultProps = {
  className: null,
  hideOnNoCategory: false,
  itemId: null,
  keyword: null,
  navParam: null,
  rounded: false,
  showInternet: false,
  data: undefined,
  loading: undefined,
  shouldShowEventOnly: false
};

export { Breadcrumbs };
